/**
 * TPG Player — Channel / linear presentation architecture.
 *
 * Platform owns EPG, schedule authority, and what program is "on now"
 * (Sprint 9: ProgramScheduleResolver + /channels/now).
 * Player owns channel presentation (live surface, DVR window UI, slate).
 */

import { MediaPresentationKind, createMediaAsset } from "./MediaAsset";

export var ChannelKind = Object.freeze({
  LIVE: "live",
  LINEAR: "linear",
  FAST: "fast",
  SIMULIVE: "simulive",
  AUDIO: "audio",
});

/**
 * @typedef {object} ChannelProgram
 * @property {string} [id]
 * @property {string} [title]
 * @property {string} [startAt]
 * @property {string} [endAt]
 * @property {object} [asset]  MediaAsset for this slot
 */

/**
 * @typedef {object} ChannelState
 * @property {string} id
 * @property {string} kind
 * @property {string} [title]
 * @property {object|null} currentProgram
 * @property {object|null} nextProgram
 * @property {number|null} dvrWindowSeconds
 * @property {object|null} slate  companion-like holding image when offline
 * @property {object|null} stream  primary live MediaResource
 */

/**
 * @param {object} input
 * @returns {ChannelState}
 */
export function createChannel(input) {
  input = input || {};
  return {
    id: input.id || null,
    kind: input.kind || ChannelKind.LIVE,
    title: input.title || "",
    currentProgram: input.currentProgram || null,
    nextProgram: input.nextProgram || null,
    dvrWindowSeconds:
      input.dvrWindowSeconds != null ? Number(input.dvrWindowSeconds) : null,
    slate: input.slate || null,
    stream: input.stream || null,
  };
}

/**
 * Build a Media Asset representing "tune to this channel now."
 * @param {ChannelState} channel
 * @returns {object}
 */
export function mediaAssetFromChannel(channel) {
  channel = channel || createChannel({});
  var programAsset =
    channel.currentProgram && channel.currentProgram.asset
      ? createMediaAsset(channel.currentProgram.asset)
      : null;
  return createMediaAsset({
    id: channel.id,
    presentationKind: MediaPresentationKind.CHANNEL,
    sourceType: "live_ingest",
    title: (programAsset && programAsset.title) || channel.title || "",
    primary: channel.stream || (programAsset && programAsset.primary) || null,
    companion: channel.slate
      ? { image: channel.slate, audio: null, audioOptional: true }
      : null,
    schedule: {
      channelId: channel.id,
      dvrWindowSeconds: channel.dvrWindowSeconds,
      eventStartAt:
        channel.currentProgram && channel.currentProgram.startAt
          ? channel.currentProgram.startAt
          : null,
      eventEndAt:
        channel.currentProgram && channel.currentProgram.endAt
          ? channel.currentProgram.endAt
          : null,
    },
    metadata: {
      channelKind: channel.kind,
      nextProgram: channel.nextProgram,
    },
    capabilities: ["live", "dvr", "channel", "analytics"],
  });
}

/**
 * Whether the player should show countdown vs live surface.
 * Platform supplies schedule times; player only renders.
 * @param {ChannelState|object} channelOrSchedule
 * @param {number} [nowMs]
 * @returns {"live"|"countdown"|"ended"|"slate"}
 */
export function resolveChannelPhase(channelOrSchedule, nowMs) {
  nowMs = nowMs != null ? nowMs : Date.now();
  var start =
    (channelOrSchedule &&
      channelOrSchedule.schedule &&
      channelOrSchedule.schedule.eventStartAt) ||
    (channelOrSchedule &&
      channelOrSchedule.currentProgram &&
      channelOrSchedule.currentProgram.startAt) ||
    null;
  var end =
    (channelOrSchedule &&
      channelOrSchedule.schedule &&
      channelOrSchedule.schedule.eventEndAt) ||
    (channelOrSchedule &&
      channelOrSchedule.currentProgram &&
      channelOrSchedule.currentProgram.endAt) ||
    null;
  var hasStream =
    (channelOrSchedule && channelOrSchedule.stream) ||
    (channelOrSchedule && channelOrSchedule.primary);

  if (start) {
    var startMs = Date.parse(start);
    if (!isNaN(startMs) && nowMs < startMs) return "countdown";
  }
  if (end) {
    var endMs = Date.parse(end);
    if (!isNaN(endMs) && nowMs > endMs) return "ended";
  }
  if (hasStream) return "live";
  return "slate";
}

export default {
  ChannelKind,
  createChannel,
  mediaAssetFromChannel,
  resolveChannelPhase,
};
